import { Injectable } from '@angular/core';
import { Subject, filter } from 'rxjs';
import { SignalrMessageAdapter } from 'src/app/core/eventhub/signalr-message-adapter';
import { EntityChangedEvent } from 'src/app/core/domain/entity-changed-event.model';
import { PortalNotification } from 'src/app/core/domain/portal-notification.model';

/**
 * Converts incoming SignalR entityChanged events from the portal-data hub
 * into PortalNotification objects and re-emits them for components to consume.
 *
 * Not every entity change is a notification. Device telemetry, for instance, arrives on a timer
 * to keep the stores current and is flagged `notify: false` server-side; forwarding it here would
 * flood the bell with entries the server never stored, so they would disappear again on reload.
 * The flag is the same one the API uses to decide whether to persist a row, which is what keeps
 * the live bell and the stored history in agreement.
 *
 * The emitted notification mirrors the stored row: it carries the same id (so it can be marked
 * read straight away) and the `issuedBy` of whoever caused it (so a member's own action does not
 * badge them — see NavBadgeService for the same rule on the REST side).
 */
@Injectable({ providedIn: 'root' })
export class DataEventNotificationService {
    private readonly _notification$ = new Subject<PortalNotification>();

    /** Emits once per incoming entity-change event that is meant for the bell. */
    readonly notification$ = this._notification$.asObservable();

    constructor(signalr: SignalrMessageAdapter) {
        signalr.entityChanged$.pipe(
            // A failed command shows as a transient error alert, never as a bell entry. The API
            // also flags it notify:false; the explicit check keeps that guarantee local.
            filter(e => e.notificationType !== 'CommandFailed'),
            filter(e => e.notify !== false),
        ).subscribe((event) =>
            this._notification$.next(this.toNotification(event))
        );
    }

    private toNotification(event: EntityChangedEvent): PortalNotification {
        return {
            // Falls back to a local id only for events from an API that does not send one yet;
            // such an entry cannot be marked read individually and clears on the next refetch.
            id: event.notificationId ?? crypto.randomUUID(),
            type: event.notificationType,
            title: event.title,
            message: event.message,
            entityId: event.entityId,
            isRead: false,
            createdAt: event.timestamp ?? new Date().toISOString(),
            issuedBy: event.issuedBy || null,
        };
    }
}
